import { S25Const } from "../../util/s25-const";
import { Item } from "../../pojo/Item";
import { OptUtil } from "../s25-opt/s25.opt.util";
import { S25Util } from "../../util/s25-util";
import { ContactService } from "../../services/contact.service";
import { FlsService } from "../../services/fls.service";
import { UserprefService } from "../../services/userpref.service";
import { PreferenceService } from "../../services/preference.service";
import { CacheRepository } from "../../decorators/cache.decorator";
import { AccessLevels, Fls, hasFullFls, isMinFls } from "../../pojo/Fls";
import { S25Help } from "../s25-help/s25.help.service";
import { ContactI } from "../../pojo/ContactI";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { Debounce } from "../../decorators/debounce.decorator";
import { Contact } from "../../pojo/Contact";
import { S25ObjectDetailsGeneralComponent } from "./general-details/s25.object.details.general.component";
import { S25ContactDetailsUserAccountComponent } from "./contact/user-account/s25.contact.details.user.account";
import { S25ObjectDetailsEditableTextComponent } from "./s25.object.details.editable.text.component";
import { S25EditableContactOrgsComponent } from "./contact/editable-orgs/s25.editable.contact.orgs.component";
import { S25MasqueradeRolesComponent } from "../S25-masquerade/masquerade.roles.component";
import { S25ObjectDetailsAddressComponent } from "./address/s25.object.details.address.component";
import { Proto } from "../../pojo/Proto";
import { S25ObjectDetailsCustomAttrComponent } from "./custom-attrs/s25.object.details.custom.attr.component";
import { OrganizationService, UpdatedContactRoles } from "../../services/organization.service";
import { ObjectPermissionService } from "../../services/object.permission.service";
import { OrganizationI } from "../../pojo/OrganizationI";
import { ObjectDetailsUtil } from "../../util/object.details.util";
import { AddressType } from "../../pojo/AddressI";
import { S25EditablePropertyComponent } from "../s25-editable-property/s25.editable.property.component";
import { S25NotificationPolicyComponent } from "../s25-notification-policy/s25.notification.policy.component";
import { S25OrgPartitionPrefsComponent } from "./organization/s25.org.partition.prefs.component";
import { SearchService } from "../../services/search/search.service";
import { S25OrgPartitionPrefsService } from "./organization/s25.org.partition.prefs.service";
import { S25OrgContactRolesComponent } from "./organization/s25.org.contact.roles.component";
import { Space, SpaceService } from "../../services/space.service";
import { Location } from "../../pojo/Location";
import { Attribute, Category, Organization, Requirements, Role, WsDataI } from "../s25-event/EventMicroI";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { EventData, EventService } from "../../services/event.service";
import { ResourceService } from "../../services/resource.service";
import { S25LocationDetailsHoursComponent } from "./location/s25.location.details.hours.component";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25LocationDetailsLayoutsComponent } from "./location/s25.location.details.layouts.component";
import { S25ObjectDetailsRelationshipsComponent } from "./s25.object.details.relationships.component";
import { S25ObjectDetailsItemPlainComponent } from "./s25.object.details.item.plain.component";
import { BuildingService } from "../../services/building.service";
import { S25EChartsComponent } from "../s25-charts/s25.echarts.component";
import { S25LocationDetailsLayoutDetailsComponent } from "./location/s25.location.details.layout.details.component";
import { Injectable, signal } from "@angular/core";
import { S25GoogleMapComponent } from "../../standalone/s25.google.maps.component";
import { S25EditableInventoryComponent } from "./resource/s25.editable.inventory.component";
import { RelationshipI, StockI } from "../../pojo/Resource";
import { CustomAttribute, EventCategory, EventRole } from "../../pojo/EventI";
import { S25ItemI } from "../../pojo/S25ItemI";
import { ObjDetailsData } from "./s25.object.details.component";
import { EventMicroService } from "../../services/event.micro.service";
import { RoleService } from "../../services/role.service";
import { AttachmentService, FileItem } from "../../services/attachment.service";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { Event } from "../../pojo/Event";
import { Task } from "../../pojo/Task";
import { EventInfoComponent } from "./event/event.info.component";
import { NameService } from "../../services/name.service";
import NumericalBoolean = Proto.NumericalBoolean;
import Feature = Location.Feature;
import Relationship = Location.Relationship;
import Layout = Location.Layout;
import Workflow = Event.Workflow;
import States = Task.States;

@Injectable()
export class S25ObjectDetailsService {
    private _selectedLayout = signal<Layout | null>(null);
    private _layouts = signal<Layout[]>([]);
    private _eventOwner = signal<S25ItemI | null>(null);

    public selectedLayout = this._selectedLayout.asReadonly();
    public layouts = this._layouts.asReadonly();
    public eventOwner = this._eventOwner.asReadonly();

    setSelectedLayout(selectedLayout: Layout) {
        this._selectedLayout.set(selectedLayout);
    }

    setLayouts(layouts: Layout[]) {
        this._layouts.set(layouts);
    }

    onLayoutChange(layout: Layout) {
        this._layouts.update((prev) => {
            const newLayouts = prev.filter((l) => l.layoutId !== layout.layoutId);
            return [...newLayouts, layout];
        });
        this._selectedLayout.set(layout);
    }

    setSelectedLayoutById(id: number) {
        this._selectedLayout.set(this.layouts().find((layout) => layout.layoutId === id) ?? null);
    }

    setEventOwner(owner: S25ItemI) {
        this._eventOwner.set(owner);
    }

    @Debounce(500)
    public static async getObjDetailsData(itemId: number, itemTypeId: Item.Id): Promise<ObjDetailsData | undefined> {
        switch (itemTypeId) {
            case Item.Ids.Contact:
                return S25ObjectDetailsService.getContactDetailsData(itemId);
            case Item.Ids.Organization:
                return S25ObjectDetailsService.getOrganizationDetailsData(itemId);
            case Item.Ids.Location:
                return S25ObjectDetailsService.getLocationDetailsData(itemId);
            case Item.Ids.Resource:
                return S25ObjectDetailsService.getResourceDetailsData(itemId);
            case Item.Ids.Event:
                return S25ObjectDetailsService.getEventDetailsData(itemId);
        }
    }

    private static getSearchQuery(itemId: number, itemTypeId: Item.Id): string {
        return `&${S25Const.typeId2ItemIdB[itemTypeId]}=${itemId}`;
    }

    private static async getEventDetailsData(itemId: number): Promise<ObjDetailsData> {
        const [detailResp, eventDataResp, editPerms, editPref, fileResp, flsResp, loginResp] = await Promise.allSettled(
            [
                EventMicroService.getEventDetailById([itemId], "T", "all"),
                EventService.getEventInclude(itemId, ["workflow", "relationships", "reservations"]),
                ObjectPermissionService.canEdit(itemId, Item.Ids.Event),
                PreferenceService.getPreferences(["EventShowInlineEdit"]),
                AttachmentService.getFileList(Item.Ids.Event, itemId),
                FlsService.getFls(),
                UserprefService.getLoggedIn(),
            ],
        );
        const fls = (flsResp.status === "fulfilled" && flsResp.value) || undefined,
            canEdit =
                editPerms.status === "fulfilled" &&
                editPerms.value &&
                editPref.status === "fulfilled" &&
                editPref.value.EventShowInlineEdit?.value === "T" &&
                isMinFls(fls?.WEBSECURITY, AccessLevels.Edit),
            detailData = detailResp.status === "fulfilled" && detailResp.value,
            files =
                fileResp.status === "fulfilled"
                    ? S25Util.array.forceArray(fileResp.value).filter((f) => !f.hasOwnProperty("cust_atrb_id"))
                    : [],
            isLoggedIn = loginResp.status === "fulfilled" && loginResp.value,
            eventData = (eventDataResp.status === "fulfilled" && eventDataResp.value) || undefined,
            formattedData =
                (detailData &&
                    (await S25ObjectDetailsService.formatEventDetailData(
                        itemId,
                        detailData,
                        eventData,
                        files,
                        fls,
                        isLoggedIn,
                    ))) ||
                undefined;
        return {
            panels: [
                ...(formattedData
                    ? [
                          {
                              header: "General",
                              id: "general",
                              section: 0,
                              sectionOrder: 1,
                              content: {
                                  component: S25ObjectDetailsGeneralComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Event,
                                      model: formattedData.general,
                                      canEdit: canEdit,
                                  },
                              },
                          },
                          ...(isMinFls(fls?.TASK_LIST, AccessLevels.Read)
                              ? [
                                    {
                                        header: "Tasks Completed",
                                        id: "tasks",
                                        section: 0,
                                        sectionOrder: 2,
                                        content: {
                                            component: S25ObjectDetailsItemPlainComponent,
                                            inputs: {
                                                itemId: itemId,
                                                itemTypeId: Item.Ids.Event,
                                                value: formattedData.tasks,
                                                type: "tasks",
                                            },
                                        },
                                    },
                                ]
                              : []),
                          {
                              header: "Event Relationships",
                              id: "relationships",
                              section: 0,
                              sectionOrder: 3,
                              content: {
                                  component: S25ObjectDetailsRelationshipsComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Event,
                                      relationships: formattedData.relationships?.data,
                                      objType: "events",
                                      eventName: formattedData.general?.name ?? "",
                                      showSearchLink: formattedData.relationships?.showSearchLink,
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Event Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Event,
                                canEdit: canEdit,
                                properties: formattedData?.categories,
                                propType: "eventCategories",
                                propPayloadType: "category",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                ...(formattedData
                    ? [
                          {
                              header: "Custom Attributes",
                              id: "attributes",
                              section: 1,
                              sectionOrder: 2,
                              content: {
                                  component: S25ObjectDetailsCustomAttrComponent,
                                  inputs: {
                                      customAttrs: formattedData.attributes,
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Event,
                                      canEdit: canEdit,
                                      eventTypeId: formattedData.eventTypeId,
                                  },
                              },
                          },
                          {
                              header: "Event Info",
                              id: "info",
                              section: 1,
                              sectionOrder: 3,
                              content: {
                                  component: EventInfoComponent,
                                  inputs: {
                                      eventInfo: formattedData.info,
                                      itemId: itemId,
                                      canEdit: canEdit,
                                      requirements: formattedData.info?.requirements ?? [],
                                  },
                              },
                          },
                      ]
                    : []),
            ],
        };
    }

    private static async formatEventDetailData(
        itemId: number,
        detailData: { content: WsDataI },
        eventData: EventData | undefined,
        files: FileItem[],
        fls: Fls | undefined,
        isLoggedIn: boolean,
    ) {
        const data = detailData.content?.data?.items?.[0] ?? {},
            expandedInfo = detailData.content?.expandedInfo ?? {},
            primaryOrgId = data.organizations?.primary,
            eventTypeId = data.context?.typeId,
            [rolesResp, ownerResp] = await Promise.allSettled([
                !!eventTypeId && RoleService.getEventTypeRoles(eventTypeId),
                (!isLoggedIn || isMinFls(fls?.CU_CONTACT, AccessLevels.Read)) && EventService.getOwnerSimple(itemId),
            ]),
            eventRoles = (rolesResp.status === "fulfilled" && rolesResp.value) || undefined,
            owner = (ownerResp.status === "fulfilled" && ownerResp.value) || undefined,
            profiles = S25Util.array.forceArray(data.profiles),
            relatedEvents =
                eventData?.content?.map((event) => ({
                    itemId: event.content_event_id,
                    itemName: event.content_event_name,
                })) ?? [],
            boundEvents =
                eventData?.profile?.flatMap(
                    (profile) =>
                        profile.binding_reservation?.map((rsrv) => ({
                            itemId: rsrv.bound_event_id,
                            itemName: rsrv.bound_name,
                        })) ?? [],
                ) ?? [],
            [roles, categories, attributes, requirements, datePref] = await Promise.all([
                ObjectDetailsUtil.filterMasterDefinitions("getEventRoles", data.roles ?? [], "roleId", [true]),
                ObjectDetailsUtil.filterMasterDefinitions("getEventCategories", data.categories ?? [], "categoryId", [
                    true,
                ]),
                ObjectDetailsUtil.filterMasterDefinitions(
                    "getEventCustomAttributes",
                    data.attributes ?? [],
                    "attributeId",
                    [true],
                ),
                ObjectDetailsUtil.filterMasterDefinitions(
                    "getEventRequirements",
                    data.requirements ?? [],
                    "requirementId",
                    [true],
                ),
                UserprefService.getS25Dateformat(),
            ]),
            formattedAttrs = await S25ObjectDetailsService.formatCustAttrObj(attributes, expandedInfo.attributes);
        return {
            general: {
                name: data.name ?? "",
                title: data.title ?? "",
                eventType: {
                    itemId: eventTypeId,
                    itemName:
                        expandedInfo.eventTypes?.find((type) => type.typeId === eventTypeId)?.typeName ??
                        S25Const.private,
                },
                primaryOrg: {
                    itemId: primaryOrgId,
                    itemName:
                        expandedInfo.organizations?.find((org) => primaryOrgId === org.organizationId)
                            ?.organizationName ?? S25Const.private,
                },
                addtlOrgs:
                    data.organizations?.ancillary?.map((org: Organization) => ({
                        itemId: org.organizationId,
                        itemName:
                            expandedInfo.organizations?.find((orgData) => org.organizationId === orgData.organizationId)
                                ?.organizationName ?? S25Const.private,
                    })) ?? [],
                contactRoles: {
                    assignedRoles:
                        roles
                            ?.sort(
                                (a: Role & { sort_order: number }, b: Role & { sort_order: number }) =>
                                    a.sort_order - b.sort_order,
                            )
                            ?.map((role: Role) => {
                                const contact = expandedInfo.contacts?.find(
                                    (contactData) => contactData.contactId === role.contactId,
                                );
                                return {
                                    itemId: role.roleId,
                                    itemLabel:
                                        expandedInfo.roles?.find((roleData) => roleData.roleId === role.roleId)
                                            ?.roleName ?? "",
                                    contact: {
                                        itemId: role.contactId,
                                        itemName:
                                            `${contact?.firstName ?? ""} ${contact?.familyName ?? S25Const.private}`.trim(),
                                    },
                                };
                            }) ?? [],
                    eventTypeRoles:
                        eventRoles?.map((role: EventRole) => ({
                            roleId: role.role_id,
                            roleName: role.role_name,
                        })) ?? [],
                },
                headCount: profiles.map((profile) => ({
                    name: profile.name,
                    profileId: profile.profileId,
                    registered: profile.registeredCount ?? 0,
                    expected: profile.expectedCount ?? 0,
                })),
                ...(isMinFls(fls?.EVENT_TEXT, AccessLevels.Read) && {
                    text: {
                        description: data.text?.[0]?.description ?? "",
                        comments: profiles.map((profile) => ({
                            name: profile.name,
                            profileId: profile.profileId,
                            comments: profile.comments ?? "",
                            fadePreview: signal((profile.comments?.length ?? 0) > 350),
                        })),
                        note: data.text?.[0]?.note ?? "",
                        confirmation: data.text?.[0]?.confirmation ?? "",
                    },
                }),
                attachments: {
                    files: files || [],
                    itemTypeId: Item.Ids.Event,
                    itemId: itemId,
                    addAction: (file: S25File) => AttachmentService.postFile(Item.Ids.Event, itemId, file.fileRef),
                    removeAction: (file: S25File) => AttachmentService.delFile(Item.Ids.Event, itemId, file.itemName),
                },
            },
            tasks: [
                {
                    label: "Approvals",
                    value: S25ObjectDetailsService.getCompletedTaskCount(
                        S25Util.array.forceArray(eventData?.approval),
                        Workflow.ApprovalTypeIds.Notification,
                    ),
                },
                {
                    label: "Assignments",
                    value: S25ObjectDetailsService.getCompletedTaskCount(
                        S25Util.array.forceArray(eventData?.approval),
                        Workflow.ApprovalTypeIds.Assignment,
                    ),
                },
                { label: "To Do", value: S25ObjectDetailsService.getCompletedTaskCount(eventData?.todo ?? []) },
            ],
            relationships: {
                showSearchLink: !!relatedEvents.length,
                data: [
                    ...(relatedEvents.length ? [["Related Events", null, relatedEvents]] : []),
                    ...(boundEvents.length ? [["Bound Events", null, boundEvents]] : []),
                ],
            },
            categories:
                categories
                    ?.filter((category: Category) => category.inheritCode >= 0)
                    ?.sort((a: Category, b: Category) => a.sort_order - b.sort_order)
                    ?.map((category: Category) => ({
                        itemId: category.categoryId,
                        itemName:
                            expandedInfo.categories?.find((catData) => catData.categoryId === category.categoryId)
                                ?.categoryName ?? "",
                    })) ?? [],
            attributes: formattedAttrs,
            info: {
                created: S25Datefilter.transform(eventData?.creation_dt ?? "", datePref),
                locator: data.eventLocator ?? "",
                cabinet: eventData?.cabinet_name ?? "",
                folder: expandedInfo.parentNodes?.[0]?.name ?? "",
                externalId: data.externalId ?? "",
                requirements:
                    requirements
                        ?.sort((a: Requirements, b: Requirements) => a.sort_order - b.sort_order)
                        ?.map((req: Requirements) => ({
                            itemId: req.requirementId,
                            itemName:
                                expandedInfo.requirements?.find(
                                    (reqData: any) => reqData.requirementId === req.requirementId,
                                )?.requirementName ?? "",
                            comment: req.comments ?? "",
                            quantity: req.quantity ?? 0,
                        })) ?? [],
                owner: { itemId: owner?.owner_cont_id ?? 0, itemName: owner?.owner_name ?? S25Const.private },
            },
            eventTypeId: eventTypeId,
        };
    }

    private static async getResourceDetailsData(itemId: number): Promise<ObjDetailsData> {
        const [detailData, fls, canEdit, editPref, isLoggedIn] = await Promise.all([
            ResourceService.getResource(itemId),
            FlsService.getFls(),
            ObjectPermissionService.canEdit(itemId, Item.Ids.Resource),
            PreferenceService.getPreferences(["ResourceShowInlineEdit"]),
            UserprefService.getLoggedIn(),
        ]);
        const hasEditPerms = canEdit && editPref?.ResourceShowInlineEdit?.value === "T";
        if (detailData) {
            const [categories, attributes] = await Promise.all([
                ObjectDetailsUtil.filterMasterDefinitions(
                    "getResourceCategories",
                    detailData.category ?? [],
                    "category_id",
                ),
                ObjectDetailsUtil.filterMasterDefinitions(
                    "getResourceCustomAttributes",
                    detailData.custom_attribute ?? [],
                    "attribute_id",
                ),
            ]);
            detailData.custom_attribute = attributes;
            detailData.category = categories;
        }
        const relMap = detailData?.relationship
            ?.sort((a: RelationshipI, b: RelationshipI) =>
                a.related_resource.localeCompare(b.related_resource, undefined, { sensitivity: "base" }),
            )
            ?.reduce((typeMap: Record<string, [string, string, S25ItemI[]]>, rel: RelationshipI) => {
                const relProp = rel.relationship_type
                    .split(" ")
                    .map((type, index) => {
                        if (index === 0) return type[0].toLowerCase() + type.slice(1);
                        return type;
                    })
                    .join("");
                if (!typeMap[relProp]) {
                    typeMap[relProp] = [rel.relationship_type, relProp, [{ itemId: rel.target_resource_id }]];
                } else {
                    typeMap[relProp][2].push({ itemId: rel.target_resource_id });
                }
                return typeMap;
            }, {});
        return {
            panels: [
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Resource,
                            canEdit: hasEditPerms,
                            data: detailData?.comments ?? "",
                            prop: "comments",
                            type: "richtext",
                        },
                    },
                },
                {
                    header: "Default Instructions",
                    id: "instructions",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Resource,
                            canEdit: hasEditPerms,
                            data: detailData?.instructions ?? "",
                            prop: "instructions",
                            type: "textarea",
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: (detailData?.custom_attribute ?? []).map((item: CustomAttribute) =>
                                CustomAttributeService.toCustAttrObj(item),
                            ),
                            itemId: itemId,
                            itemTypeId: Item.Ids.Resource,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                ...(isLoggedIn && hasFullFls(fls.RESOURCE_NOTIFY)
                    ? [
                          {
                              header: "Notification Policy",
                              id: "notPolicy",
                              section: 0,
                              sectionOrder: 4,
                              content: {
                                  component: S25NotificationPolicyComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Resource,
                                      type: "resource",
                                      inModal: false,
                                      canEdit: hasEditPerms,
                                      editModeOff: !hasEditPerms,
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Inventory",
                    id: "inventory",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25EditableInventoryComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: hasEditPerms,
                            stock:
                                detailData?.stock?.map((item: StockI) => ({
                                    itemId: item.version_nbr,
                                    count: item.stock_level,
                                    startDt: item.stock_start_dt,
                                    endDt: item.stock_end_dt,
                                    status: item.status,
                                })) ?? [],
                        },
                    },
                },
                {
                    header: "Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Resource,
                                canEdit: hasEditPerms,
                                properties:
                                    detailData?.category?.map((category: EventCategory) => ({
                                        itemId: category.category_id,
                                        itemName: category.category_name,
                                    })) ?? [],
                                propType: "resourceCategories",
                                propPayloadType: "category",
                                hasSelectAll: false,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Related Resources",
                    id: "related",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsRelationshipsComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Resource,
                            canEdit: hasEditPerms,
                            relationships: Object.values(relMap ?? {}),
                            objType: "resources",
                        },
                    },
                },
            ],
        };
    }

    private static async getLocationDetailsData(itemId: number): Promise<ObjDetailsData> {
        const [detailResp, canEdit, flsResp, isLoggedIn, is24HourResp, dateTimePrefResp, editPref] =
            await Promise.allSettled([
                SpaceService.getSpace(itemId),
                ObjectPermissionService.canEdit(itemId, Item.Ids.Location),
                FlsService.getFls(),
                UserprefService.getLoggedIn(),
                UserprefService.getIs24HourTime(),
                UserprefService.getS25DateTimeformat(),
                PreferenceService.getPreferences(["SpaceShowInlineEdit", "Eval25"]),
            ]);
        const detailData = (detailResp.status === "fulfilled" && detailResp.value) || undefined,
            spaceDetails = detailData?.data?.items?.[0],
            hasEditPerms =
                canEdit.status === "fulfilled" &&
                canEdit.value &&
                editPref.status === "fulfilled" &&
                editPref.value.SpaceShowInlineEdit?.value === "T",
            eval25Enabled = editPref.status === "fulfilled" && editPref.value.Eval25?.value === "true",
            is24Hour = is24HourResp.status === "fulfilled" && is24HourResp.value,
            dateTimePref = dateTimePrefResp.status === "fulfilled" && dateTimePrefResp.value,
            fls = (flsResp.status === "fulfilled" && flsResp.value) || undefined,
            formatData = await S25ObjectDetailsService.formatLocationDetailsData(detailData, fls);
        return {
            panels: [
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            data: spaceDetails?.comments ?? "",
                            prop: "comments",
                            type: "richtext",
                        },
                    },
                },
                {
                    header: "Default Instructions",
                    id: "instructions",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            data: spaceDetails?.instructions ?? "",
                            prop: "instructions",
                            type: "textarea",
                        },
                    },
                },
                {
                    header: "Features",
                    id: "features",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Location,
                                canEdit: hasEditPerms,
                                properties: formatData.features,
                                propType: "locationFeatures",
                                propPayloadType: "feature",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: formatData.attributes,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Hours",
                    id: "hours",
                    section: 0,
                    sectionOrder: 5,
                    content: {
                        component: S25LocationDetailsHoursComponent,
                        inputs: {
                            hours: spaceDetails?.hours,
                            itemId: itemId,
                            canEdit: hasEditPerms,
                            is24Hour: is24Hour,
                        },
                    },
                },
                {
                    header: "Layouts",
                    id: "layouts",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25LocationDetailsLayoutsComponent,
                        inputs: {
                            layouts: formatData.layouts,
                            itemId: itemId,
                            canEdit: hasEditPerms,
                            maxCapacity: spaceDetails?.maxCapacity,
                        },
                    },
                },
                {
                    header: "Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Location,
                                canEdit: hasEditPerms,
                                properties: formatData.categories,
                                propType: "locationCategories",
                                propPayloadType: "category",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Related Spaces",
                    id: "related",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsRelationshipsComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            canEdit: hasEditPerms,
                            relationships: formatData.relationships,
                            objType: "locations",
                        },
                    },
                },
                {
                    header: "Location Scheduler",
                    id: "scheduler",
                    section: 1,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "scheduler",
                            canEdit: hasEditPerms,
                            value:
                                spaceDetails?.roles?.map((role) => ({
                                    itemId: role.contactId,
                                    itemTypeId: Item.Ids.Contact,
                                    itemName: formatData?.schedulerName || S25Const.private,
                                    primaryId: role.roleId,
                                }))?.[0] ?? {},
                        },
                    },
                },
                {
                    header: "Partition",
                    id: "partition",
                    section: 1,
                    sectionOrder: 5,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "partition",
                            canEdit: hasEditPerms,
                            value: formatData?.partition,
                        },
                    },
                },
                {
                    header: "Fill Ratio Percentage",
                    id: "ratio",
                    section: 1,
                    sectionOrder: 6,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "ratio",
                            canEdit: hasEditPerms,
                            value: spaceDetails?.fillRatio,
                        },
                    },
                },
                ...(hasEditPerms
                    ? [
                          {
                              header: "Express",
                              id: "express",
                              section: 1,
                              sectionOrder: 7,
                              content: {
                                  component: S25ObjectDetailsItemPlainComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Location,
                                      type: "express",
                                      canEdit: hasEditPerms,
                                      value:
                                          spaceDetails?.dirSched?.map((schedule) => ({
                                              itemId: schedule.directScheduleTypeId,
                                              itemName: schedule.directScheduleTypeName,
                                          }))?.[0] ?? {},
                                      altVal: S25Util.minutesToISODuration(
                                          spaceDetails?.dirSched?.[0]?.directScheduleDuration ?? 0,
                                      ),
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Building",
                    id: "building",
                    section: 1,
                    sectionOrder: 8,
                    content: {
                        component: S25ObjectDetailsItemPlainComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Location,
                            type: "building",
                            canEdit: hasEditPerms,
                            value: formatData?.building,
                            altVal: {
                                imageId: (formatData?.building as any)?.imageId,
                                imageUrl: (formatData?.building as any)?.url,
                            },
                        },
                    },
                },
                ...(spaceDetails?.roomRating?.length && eval25Enabled
                    ? [
                          {
                              header: "Location Satisfaction",
                              id: "eval25Rating",
                              section: 2,
                              sectionOrder: 1,
                              content: {
                                  component: S25EChartsComponent,
                                  inputs: {
                                      roomData: spaceDetails.roomRating.sort(
                                          (a, b) => +new Date(a.endDt) - +new Date(b.endDt),
                                      ),
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Layout Details",
                    id: "layoutDetails",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25LocationDetailsLayoutDetailsComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Map",
                    id: "map",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25GoogleMapComponent,
                        inputs: {
                            latitude: formatData.coordinates?.latitude,
                            longitude: formatData.coordinates?.longitude,
                            zoom: 15,
                        },
                    },
                },
                ...(isLoggedIn && hasFullFls(fls.SPACE_NOTIFY)
                    ? [
                          {
                              header: "Notification Policy",
                              id: "notPolicy",
                              section: 2,
                              sectionOrder: 4,
                              content: {
                                  component: S25NotificationPolicyComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Location,
                                      type: "location",
                                      inModal: false,
                                      canEdit: hasEditPerms,
                                      editModeOff: !hasEditPerms,
                                  },
                              },
                          },
                      ]
                    : []),
            ],
            dateStamp: S25Datefilter.transform(S25Util.date.currentDateTime(), dateTimePref),
        };
    }

    private static async formatLocationDetailsData(
        detailData:
            | {
                  data: { items: [Space] };
                  expandedInfo: Partial<Space>;
              }
            | undefined,
        fls: Fls | undefined,
    ) {
        const { features, attributes, layouts, categories, relationships, buildingId, roles } =
                detailData?.data?.items?.[0] ?? {},
            {
                features: featureDetails,
                attributes: attributeDetails,
                layouts: layoutDetails,
                categories: categoryDetails,
                partition,
                building,
                contacts,
            } = detailData?.expandedInfo ?? {},
            hasContactPerms = isMinFls(fls.CU_CONTACT, AccessLevels.Read),
            buildingImageData = !!buildingId && (await BuildingService.getImageUrl(buildingId)),
            formattedAttrs = await S25ObjectDetailsService.formatCustAttrObj(
                attributes as Attribute[],
                attributeDetails as Attribute[],
            );
        //ANG-6308 contacts service requires auth and we should already have the info we need
        let schedulerName;
        if (hasContactPerms && roles?.length) {
            const schedulerContId = roles?.[0]?.contactId;
            const contact = S25Util.array.forceArray(contacts).find((c) => c.contactId === schedulerContId);
            if (contact) {
                schedulerName = `${contact.firstName ?? ""} ${contact.familyName ?? ""}`;
            } else {
                const isLoggedIn = await UserprefService.getLoggedIn();
                schedulerName = isLoggedIn ? await ContactService.getContactName(roles[0].contactId) : S25Const.private;
            }
        }
        return {
            features:
                featureDetails?.map((feature: Feature) => {
                    const featureObj: Feature | undefined = features?.find(
                        (featureData: Feature) => featureData.featureId === feature.featureId,
                    );
                    return {
                        itemId: feature.featureId,
                        itemName: feature.featureName ?? "",
                        quantity: featureObj?.quantity ?? 0,
                    };
                }) ?? [],
            attributes: formattedAttrs,
            layouts:
                layouts
                    ?.map((layout) => {
                        const layoutObj = layoutDetails?.find((layoutData) => layoutData.layoutId === layout.layoutId);
                        return { ...layout, layoutName: layoutObj?.layoutName ?? "" };
                    })
                    ?.sort((a, b) => a.layoutName.localeCompare(b.layoutName, undefined, { sensitivity: "base" })) ??
                [],
            categories:
                categoryDetails?.map((category: Category) => {
                    return {
                        itemId: category.categoryId,
                        itemName: category.categoryName ?? "",
                    };
                }) ?? [],
            relationships: Object.entries(relationships ?? {}) //process so an array of type name and list of associated spaces, ["Subdivision Of", "subdivisionOf", [{itemId: 123}]]
                .filter(([_, spaces]) => !!spaces.length)
                .map(([relType, spaces]) => {
                    const titleCaseRelType = relType
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())
                        .replace(/\b\w/g, (char) => char.toUpperCase());
                    return [
                        titleCaseRelType,
                        relType,
                        spaces.map((space: Relationship) => ({
                            itemId: space.spaceId,
                        })),
                    ];
                }),
            partition: { itemId: partition?.[0]?.partitionId, itemName: partition?.[0]?.partitionName ?? "" },
            building:
                building?.map((building) => ({
                    itemId: building.buildingId,
                    itemName: building.buildingName ?? "",
                    ...(!!buildingImageData && {
                        imageId: buildingImageData?.imageId,
                        imageUrl: buildingImageData?.url,
                    }),
                }))?.[0] ?? {},
            coordinates: {
                latitude: Number(attributes?.find((attr) => attr.attributeId === -14)?.value),
                longitude: Number(attributes?.find((attr) => attr.attributeId === -15)?.value),
            },
            schedulerName: schedulerName,
        };
    }

    private static async getOrganizationDetailsData(itemId: number): Promise<ObjDetailsData> {
        const [detailData, fls, isLoggedIn, canEdit, editPref] = await Promise.all([
            OrganizationService.getOrganization(itemId),
            FlsService.getFls(),
            UserprefService.getLoggedIn(),
            ObjectPermissionService.canEdit(itemId, Item.Ids.Organization),
            PreferenceService.getPreferences(["AcctShowInlineEdit"]),
        ]);
        const hasEditPerms = canEdit && editPref?.AcctShowInlineEdit?.value === "T";
        const orgDetails = await S25ObjectDetailsService.formatOrgDetailsData(
            detailData,
            fls,
            hasEditPerms,
            isLoggedIn,
        );
        return {
            panels: [
                {
                    header: "General",
                    id: "general",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsGeneralComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            model: orgDetails.general,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                            data: orgDetails.comments.comments,
                            canEditComments: orgDetails.comments.canEditComments,
                            prop: "comments",
                            type: "richtext",
                        },
                    },
                },
                {
                    header: "Administrative Address",
                    id: "adminAddr",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: orgDetails.editableAdminAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Billing Address",
                    id: "billingAddr",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: orgDetails.editableBillingAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                {
                    header: "Categories",
                    id: "categories",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25EditablePropertyComponent,
                        inputs: {
                            model: {
                                itemId: itemId,
                                itemTypeId: Item.Ids.Organization,
                                canEdit: hasEditPerms,
                                properties: orgDetails.categories,
                                propType: "organizationCategories",
                                propPayloadType: "category",
                                hasSelectAll: true,
                                noneText: "none",
                            },
                            useOnDone: true,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: orgDetails.attributes,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Organization,
                            canEdit: hasEditPerms,
                        },
                    },
                },
                ...(isLoggedIn && isMinFls(fls.CU_ACCT_NOTIFY, AccessLevels.Read)
                    ? [
                          {
                              header: "Notification Policy",
                              id: "notPolicy",
                              section: 1,
                              sectionOrder: 3,
                              content: {
                                  component: S25NotificationPolicyComponent,
                                  inputs: {
                                      itemId: itemId,
                                      itemTypeId: Item.Ids.Organization,
                                      type: "organization",
                                      inModal: false,
                                      canEdit: hasEditPerms && isMinFls(fls.CU_ACCT_NOTIFY, AccessLevels.Edit),
                                      editModeOff: !hasEditPerms,
                                  },
                              },
                          },
                      ]
                    : []),
                ...(isLoggedIn
                    ? [
                          {
                              header: "Partition Preferences",
                              id: "partitionPrefs",
                              section: 2,
                              sectionOrder: 1,
                              content: {
                                  component: S25OrgPartitionPrefsComponent,
                                  inputs: {
                                      modelBean: { ...orgDetails.partitionPrefs, orgId: itemId },
                                      readOnly: !hasEditPerms || !isMinFls(fls.CU_ACCT_PREF, AccessLevels.Edit),
                                      doSave: true,
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Associated Contacts",
                    id: "contacts",
                    section: 2,
                    sectionOrder: 2,
                    content: {
                        component: S25OrgContactRolesComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: hasEditPerms && isMinFls(fls.CU_CONTACT, AccessLevels.Read),
                            contacts: (detailData?.contact ?? []).map(OrganizationService.toContactRoleObj),
                            onChange: (primaryContactId: number, updatedContacts: UpdatedContactRoles) =>
                                OrganizationService.updateContactRoles([itemId], {
                                    ...updatedContacts,
                                    primaryId: primaryContactId,
                                }),
                        },
                    },
                },
            ],
        };
    }

    private static async formatOrgDetailsData(
        detailData: OrganizationI,
        fls: Fls,
        canEdit: boolean,
        isLoggedIn: boolean,
    ) {
        const prefQueryId = detailData?.space_preference?.query_id,
            adminAddress = S25Util.extractAddress(detailData.address, AddressType.Admin),
            billingAddress = S25Util.extractAddress(detailData.address, AddressType.Billing);
        let partitionSteps;
        if (isLoggedIn && prefQueryId) {
            const [data, error] = await S25Util.Maybe(SearchService.getSearchCriteria(Item.Ids.Location, prefQueryId));
            if (error) console.error(error);
            partitionSteps = data;
        }
        let orgType = S25Util.array.forceArray(detailData.organization_type).map((org) => ({
                itemId: org.type_id,
                itemName: org.type_name,
                isActive: S25Util.toBool(org.type_defn_state),
            })),
            rating = [{ itemName: detailData.rating_name, itemId: detailData.rating_id }],
            category = detailData.category ?? [],
            attribute = detailData.custom_attribute ?? [];
        const [types, ratings, categories, attributes] = await Promise.all([
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationTypes", orgType),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationRatings", rating),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationCategories", category, "category_id"),
            ObjectDetailsUtil.filterMasterDefinitions("getOrganizationCustomAttributes", attribute, "attribute_id"),
        ]);
        orgType = types;
        rating = ratings;
        category = categories;
        attribute = attributes;
        return {
            general: {
                canEditAcctCode: canEdit && hasFullFls(fls.CU_ACCT_NUMBERS),
                canEditRating: canEdit && isMinFls(fls.CU_ACCT_RATING, AccessLevels.Edit),
                type: orgType[0],
                origType: orgType[0],
                rating: rating[0],
                orgNumber: detailData.organization_number,
            },
            comments: {
                canEditComments: canEdit && isMinFls(fls.CU_COMMENTS, AccessLevels.Edit),
                comments: S25Util.s25CleanupHtml(detailData.comments ?? ""),
            },
            editableAdminAddress: {
                street: `${adminAddress.line1 ?? ""}${adminAddress.line2 ? " " + adminAddress.line2 : ""}`,
                city: adminAddress.city ?? "",
                state: adminAddress.state ?? "",
                zip: adminAddress.zip ?? "",
                country: adminAddress.country ?? "",
                phone: adminAddress.phone ?? "",
                fax: adminAddress.fax ?? "",
                addressType: AddressType.Admin,
            },
            editableBillingAddress: {
                street: `${billingAddress.line1 ?? ""}${billingAddress.line2 ? " " + billingAddress.line2 : ""}`,
                city: billingAddress.city ?? "",
                state: billingAddress.state ?? "",
                zip: billingAddress.zip ?? "",
                country: billingAddress.country ?? "",
                phone: billingAddress.phone ?? "",
                fax: billingAddress.fax ?? "",
                addressType: AddressType.Billing,
            },
            categories: category,
            attributes: attribute.map((item) => CustomAttributeService.toCustAttrObj(item)),
            partitionPrefs: {
                groups: (S25Util.propertyGet(partitionSteps, "step") ?? [])
                    .sort(S25Util.shallowSort("step_priority", true))
                    .map(S25OrgPartitionPrefsService.stepToGroup),
                queryId: prefQueryId,
            },
        };
    }

    private static async getContactDetailsData(itemId: number): Promise<ObjDetailsData> {
        const [detailData, flsResp, groupId, editPref] = await Promise.allSettled([
            ContactService.getContact(itemId),
            FlsService.getFls(),
            UserprefService.getGroupId(),
            PreferenceService.getPreferences(["ContShowInlineEdit"]),
        ]);
        const showInlineEdit = editPref.status === "fulfilled" && editPref.value.ContShowInlineEdit?.value === "T",
            fls = (flsResp.status === "fulfilled" && flsResp.value) || undefined,
            fullPerms = hasFullFls(fls?.CU_CONTACT),
            contactDetails = (detailData.status === "fulfilled" && detailData.value.contacts?.contact) ?? {},
            isAdmin = groupId.status === "fulfilled" && groupId.value === -1;
        let canEdit = fullPerms && showInlineEdit,
            canEditSecurity = hasFullFls(fls?.SECURITY) && showInlineEdit,
            username,
            warningMessage;
        const { r25user } = contactDetails;
        if (r25user && canEdit) {
            const systemUsers = ["service25", "object25"];
            username = r25user?.r25_username;
            const isSystemUser = systemUsers.includes(username.toLowerCase());
            const isInternal = username.toLowerCase() === "viewer25";
            canEdit = canEdit && !isSystemUser && (isAdmin || !isInternal);
            canEditSecurity = canEditSecurity && canEdit && !isInternal;
            warningMessage = S25ObjectDetailsService.setContactWarningAlert(isAdmin, isSystemUser, isInternal);
        }
        const details = S25ObjectDetailsService.formatContactDetailsData(contactDetails);
        return {
            warningMessage: warningMessage,
            panels: [
                ...(details
                    ? [
                          {
                              header: "General",
                              id: "general",
                              section: 0,
                              sectionOrder: 1,
                              content: {
                                  component: S25ObjectDetailsGeneralComponent,
                                  inputs: {
                                      model: details.general,
                                      itemTypeId: Item.Ids.Contact,
                                      itemId: itemId,
                                      canEdit: canEdit,
                                  },
                              },
                          },
                          {
                              header: "User Account",
                              id: "account",
                              section: 0,
                              sectionOrder: 2,
                              content: {
                                  component: S25ContactDetailsUserAccountComponent,
                                  inputs: {
                                      userData: details?.userAccount,
                                      canEdit: canEdit,
                                      canEditSecurity: canEditSecurity,
                                      itemId: itemId,
                                      isAdmin: isAdmin,
                                  },
                              },
                          },
                      ]
                    : []),
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsEditableTextComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                            data: details?.comments ?? "",
                            prop: "comments",
                            type: "richtext",
                        },
                    },
                },
                {
                    header: "Associated Organizations",
                    id: "assocOrgs",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25EditableContactOrgsComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            orgs: details?.editableOrgs,
                        },
                    },
                },
                {
                    header: "Additional Roles",
                    id: "roles",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25MasqueradeRolesComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            groupId: groupId,
                        },
                    },
                },
                {
                    header: "Work Contact Details",
                    id: "workDetails",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details?.editableWorkAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Home Contact Details",
                    id: "homeDetails",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details?.editableHomeAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 1,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: details?.attributesEditable,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
            ],
        };
    }

    private static setContactWarningAlert(isAdmin: boolean, isSystemUser: boolean, isInternalUser: boolean) {
        let message, link;
        if (isSystemUser) message = "This is a system defined contact record and cannot be edited.";
        else if (isInternalUser) {
            if (isAdmin) link = S25Help.getHelpUrl("system_users");
            const viewer25Warning = "Modifying the viewer25 account can have unintended consequences.";
            const adminOnly = "This is an internal contact, and can only be edited by an administrator.";
            message = isAdmin ? viewer25Warning : adminOnly;
        }
        if (message) return { message, link };
        return null;
    }

    private static formatContactDetailsData(contact: ContactI): Contact.DetailData {
        const {
            contact_name,
            first_name,
            last_name,
            middle_name,
            name_prefix,
            name_suffix,
            title,
            comments,
            internal_id,
            r25user,
            attribute,
            organization,
        } = contact;
        const attributes = S25Util.array.forceArray(attribute),
            organizations = S25Util.array.forceArray(organization),
            homeDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "4"),
            workDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "3");
        const {
            street_address: homeStreet,
            city: homeCity,
            state_prov: homeState,
            zip_post: homeZip,
            country: homeCountry,
            fax: homeFax,
            phone: homePhone,
            email: homeEmail,
        } = homeDetails || {};
        const {
            street_address: workStreet,
            city: workCity,
            state_prov: workState,
            zip_post: workZip,
            country: workCountry,
            fax: workFax,
            phone: workPhone,
            email: workEmail,
        } = workDetails || {};
        return {
            general: {
                contactName: contact_name,
                firstName: first_name,
                middleName: middle_name,
                lastName: last_name,
                namePrefix: name_prefix,
                nameSuffix: name_suffix,
                title: title,
                internalId: internal_id,
            },
            userAccount: {
                userName: r25user?.r25_username,
                active: r25user?.r25_defn_state as NumericalBoolean,
                securityGroup: {
                    itemName: r25user?.security_group?.security_group_name,
                    itemId: r25user?.security_group?.security_group_id,
                    itemTypeId: Item.Ids.Contact,
                },
            },
            comments: comments,
            attributesEditable: attributes.map((item) => CustomAttributeService.toCustAttrObj(item)),
            editableOrgs: organizations.map((org) => ({
                itemName: org.organization_name,
                itemId: org.organization_id,
                role: { itemId: org.role_id, itemName: org.role_name },
            })),
            editableHomeAddress: {
                street: homeStreet ?? "",
                city: homeCity ?? "",
                state: homeState ?? "",
                zip: homeZip ?? "",
                country: homeCountry ?? "",
                phone: homePhone ?? "",
                fax: homeFax ?? "",
                email: homeEmail ?? "",
                addressType: AddressType.Home,
            },
            editableWorkAddress: {
                street: workStreet ?? "",
                city: workCity ?? "",
                state: workState ?? "",
                zip: workZip ?? "",
                country: workCountry ?? "",
                phone: workPhone ?? "",
                fax: workFax ?? "",
                email: workEmail ?? "",
                addressType: AddressType.Work,
            },
        };
    }

    public static invalidateCache(itemTypeId: Item.Id) {
        return CacheRepository.invalidateByMethod(S25ObjectDetailsService.ObjMap[itemTypeId].invalidateMethod);
    }

    public static getOptBean(itemId: number, itemTypeId: Item.Id) {
        const optParams = {
            itemId: itemId,
            itemTypeId: itemTypeId,
            searchContext: S25ObjectDetailsService.getSearchQuery(itemId, itemTypeId),
        };
        return S25ObjectDetailsService.ObjMap[itemTypeId].getOptBean(null, optParams);
    }

    public static ObjMap: Partial<Record<Item.Id, ObjMapItem>> = {
        [Item.Ids.Contact]: {
            invalidateMethod: "ContactService.getContact",
            getOptBean: OptUtil.formContactDetailsBean,
        },
        [Item.Ids.Organization]: {
            invalidateMethod: "OrganizationService.getOrganization",
            getOptBean: OptUtil.formOrganizationDetailsBean,
        },
        [Item.Ids.Location]: {
            invalidateMethod: "SpaceService.getSpace",
            getOptBean: OptUtil.formLocationDetailsBean,
        },
        [Item.Ids.Resource]: {
            invalidateMethod: "ResourceService.getResource",
            getOptBean: OptUtil.formResourceDetailsBean,
        },
        [Item.Ids.Event]: {
            invalidateMethod: "EventMicroService.getEventDetailById",
            getOptBean: OptUtil.formEventDetailsBean,
        },
    };

    public static PayloadPropConst: Record<ItemPlainType, PropObj> = {
        partition: { idProp: "partitionId" },
        ratio: { idProp: "fillRatio" },
        scheduler: {
            typeProp: "roles",
            idProp: "roleId",
            altProp: "contactId",
            hasAltVal: true,
        },
        express: {
            typeProp: "directSchedule",
            idProp: "eventTypeId",
            altProp: "maxDuration",
            hasAltVal: true,
        },
        building: { idProp: "buildingId" },
        tasks: null,
    };

    private static getCompletedTaskCount(tasks: Workflow.Task[] | Workflow.ToDo[], typeId?: Workflow.ApprovalTypeIds) {
        let filteredTasks = S25Util.array.forceArray(tasks),
            completedTasks;
        if (typeId) {
            filteredTasks = filteredTasks.filter(
                (task: Workflow.Task) => task.approval_type_id === typeId && task.approval_state !== States.Cancelled,
            ) as Workflow.Task[];
            completedTasks = filteredTasks.reduce(
                (acc, task) => acc + (task.approval_state === States.Completed ? 1 : 0),
                0,
            );
        } else {
            filteredTasks = filteredTasks.filter(
                (task: Workflow.ToDo) => task.cur_todo_state !== States.Cancelled,
            ) as Workflow.ToDo[];
            completedTasks = filteredTasks.reduce(
                (acc, task) => acc + (task.cur_todo_state === States.Completed ? 1 : 0),
                0,
            );
        }
        return `${completedTasks}/${filteredTasks.length}`;
    }

    private static async getObjectNamesMap(attributes: Attribute[], attributeDetails: Attribute[]) {
        const items =
            (attributeDetails as Attribute[])
                ?.filter(
                    (attr) =>
                        !!Number(attr.dataType) &&
                        attributes?.some((attribute) => attribute.attributeId === attr.attributeId),
                )
                ?.map((attr) => {
                    const attribute = attributes?.find((attrData) => attrData.attributeId === attr.attributeId);
                    return { itemId: +attribute?.value, itemTypeId: +attr?.dataType };
                }) ?? [];
        const [objNames, err] = await S25Util.Maybe(NameService.getNames(items));
        if (err) {
            console.error(err);
            return {};
        }
        return objNames;
    }

    private static async formatCustAttrObj(attributes: Attribute[], attributeDetails: Attribute[] | undefined) {
        const objData = await S25ObjectDetailsService.getObjectNamesMap(attributes, attributeDetails);
        return (
            attributeDetails
                ?.filter((attribute) => attributes?.some((attr) => attr.attributeId === attribute.attributeId))
                ?.map((attr) => {
                    const attrObj = attributes?.find(
                            (attrData) => attrData.attributeId === attr.attributeId,
                        ) as Attribute,
                        attrType = Number(attr.dataType) || attr.dataType,
                        multiVal = S25ObjectDetailsService.formatMultiVal(attrObj?.value);
                    return {
                        custAttrId: attr.attributeId,
                        itemId:
                            typeof attrType === "number" || attrType === CustomAttributes.type.Image
                                ? attrObj?.value
                                : attr.attributeId,
                        imageId: attrType === CustomAttributes.type.Image ? attrObj?.value : null,
                        itemName:
                            typeof attrType === "number" && !!objData
                                ? (objData[`${attrObj?.value}-${attrType}`] ?? S25Const.private)
                                : multiVal
                                  ? multiVal
                                  : attrObj?.value,
                        itemLabel: attr.attributeName ?? "",
                        itemTypeId: attrType,
                        multi_val: !!multiVal,
                    };
                }) ?? []
        );
    }

    public static formatMultiVal(value: string) {
        try {
            return (
                JSON.parse(value)?.map((val: { item: string }) => ({
                    itemId: val.item,
                    itemName: val.item,
                })) ?? []
            );
        } catch (e) {
            return false;
        }
    }
}

export type ItemPlainType = "scheduler" | "partition" | "ratio" | "express" | "building" | "tasks";
export type PropObj = {
    idProp: string;
    typeProp?: string;
    altProp?: string;
    hasAltVal?: boolean;
};

type ObjMapItem = {
    invalidateMethod: string;
    getOptBean: (refreshF: any, runTimeParams: any) => Promise<any>;
};
